import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { ThemeProvider } from '@/context/ThemeContext';
import { Toaster } from '@/components/ui/sonner';
import AppLayout from '@/components/app/AppLayout';
import ChatWidget from '@/components/app/ChatWidget';
import Landing from '@/pages/Landing';
import Login from '@/pages/Login';
import Signup from '@/pages/Signup';
import AuthCallback from '@/pages/AuthCallback';
import Dashboard from '@/pages/Dashboard';
import Signals from '@/pages/Signals';
import Verification from '@/pages/Verification';
import Admin from '@/pages/Admin';
import Pricing from '@/pages/Pricing';
import { News, Analysis, Charts, Referral } from '@/pages/Misc';
import { Chat, Profile } from '@/pages/ChatProfile';
import SmartTrade from '@/pages/SmartTrade';
import Trading from '@/pages/Trading';
import '@/App.css';

function Protected({ children, adminOnly }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="text-saffron">Loading 4xview…</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return <AppLayout>{children}</AppLayout>;
}

function AppRouter() {
  const location = useLocation();
  // Handle Emergent OAuth session_id in URL fragment BEFORE any protected route runs
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/signals" element={<Protected><Signals /></Protected>} />
      <Route path="/charts" element={<Protected><Charts /></Protected>} />
      <Route path="/analysis" element={<Protected><Analysis /></Protected>} />
      <Route path="/news" element={<Protected><News /></Protected>} />
      <Route path="/chat" element={<Protected><Chat /></Protected>} />
      <Route path="/smart-trade" element={<Protected><SmartTrade /></Protected>} />
      <Route path="/trading" element={<Protected><Trading /></Protected>} />
      <Route path="/verification" element={<Protected><Verification /></Protected>} />
      <Route path="/pricing" element={<Protected><Pricing /></Protected>} />
      <Route path="/referral" element={<Protected><Referral /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/admin" element={<Protected adminOnly><Admin /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <Toaster position="bottom-right" />
            <ChatWidget />
            <AppRouter />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
